import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "tj_admin_token";
const USER_KEY = "tj_admin_user";

export const ADMIN_USERS = [
  { id: "beckett", name: "Beckett", role: "Owner" },
  { id: "jazmin", name: "Jazmin", role: "Executive Director" },
];

export const getAdminUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const id = JSON.parse(raw);
    return ADMIN_USERS.find((u) => u.id === id) || null;
  } catch {
    return null;
  }
};

export const setAdminUser = (userId) => {
  try {
    if (userId) localStorage.setItem(USER_KEY, JSON.stringify(userId));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
};

export const getAdminToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const setAdminToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

export const clearAdminToken = () => {
  setAdminToken(null);
  setAdminUser(null);
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token to all requests when present
api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-clear token on 401 so the UI can re-prompt login
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      clearAdminToken();
    }
    return Promise.reject(error);
  },
);

export const createLead = (payload) => api.post("/leads", payload).then((r) => r.data);
export const createFacilityLead = (payload) =>
  api.post("/facility-leads", payload).then((r) => r.data);
export const listLeads = () => api.get("/leads").then((r) => r.data);
export const getLead = (id) => api.get(`/leads/${id}`).then((r) => r.data);
export const updateLead = (id, payload) => api.patch(`/leads/${id}`, payload).then((r) => r.data);
export const updateLeadStatus = (id, status) =>
  api.patch(`/leads/${id}/status`, { status }).then((r) => r.data);
export const getStats = () => api.get("/leads-stats").then((r) => r.data);

export const updateLeadBilling = (id, body) =>
  api.patch(`/leads/${id}/billing`, body).then((r) => r.data);
export const sendBillingEmail = (id, body) =>
  api.post(`/leads/${id}/billing-email`, body).then((r) => r.data);
export const markLeadPaid = (id) =>
  api.post(`/leads/${id}/mark-paid`).then((r) => r.data);
export const archiveLead = (id) =>
  api.post(`/leads/${id}/archive`).then((r) => r.data);
export const restoreLead = (id) =>
  api.post(`/leads/${id}/restore`).then((r) => r.data);
export const binLead = (id) =>
  api.post(`/leads/${id}/bin`).then((r) => r.data);
export const unbinLead = (id) =>
  api.post(`/leads/${id}/unbin`).then((r) => r.data);
export const deleteLead = (id) =>
  api.delete(`/leads/${id}`).then((r) => r.data);

export const adminLogin = (password, userId) =>
  api.post("/admin/login", { password, user: userId || undefined }).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const adminGoogleSession = (sessionId, userId) =>
  api.post("/admin/google-session", { session_id: sessionId, user: userId || undefined }).then((r) => r.data);

export const GOOGLE_PENDING_USER_KEY = "tj_google_pending_user";
export const startAdminGoogleSignIn = (userId) => {
  localStorage.setItem(GOOGLE_PENDING_USER_KEY, userId);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// ---- Team Chat ----
export const listAdminUsers = () => api.get("/admin/users").then((r) => r.data);
export const listConversations = () =>
  api.get("/chat/conversations").then((r) => r.data);
export const listChatMessages = (peer, params = {}) =>
  api.get("/chat/messages", { params: { peer, ...params } }).then((r) => r.data);
export const sendChatMessage = (formData) =>
  api.post("/chat/messages", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
export const markConversationRead = (peer) =>
  api.post(`/chat/conversations/${peer}/mark-read`).then((r) => r.data);
export const getChatUnread = () => api.get("/chat/unread-count").then((r) => r.data);
export const searchChat = (q) =>
  api.get("/chat/search", { params: { q } }).then((r) => r.data);
export const getRecentChat = (limit = 5) =>
  api.get("/chat/recent", { params: { limit } }).then((r) => r.data);
export const chatAttachmentUrl = (mid) => {
  const token = getAdminToken();
  return `${API}/chat/messages/${mid}/attachment?token=${encodeURIComponent(token || "")}`;
};

// ---- Admin dashboard ----
export const getDashboardStats = () =>
  api.get("/admin/dashboard-stats").then((r) => r.data);
export const getRecentActivity = (limit = 12) =>
  api.get(`/admin/recent-activity?limit=${limit}`).then((r) => r.data);

// ---- Phase 2: Contracts ----
export const CONTRACT_STATUSES = [
  { value: "draft", label: "Draft", color: "#64748B", bg: "#F1F5F9" },
  { value: "pending_signature", label: "Pending Signature", color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  { value: "active", label: "Active", color: "#0F6D5D", bg: "rgba(15,109,93,0.12)" },
  { value: "expired", label: "Expired", color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
  { value: "cancelled", label: "Cancelled", color: "#475569", bg: "#E2E8F0" },
];

export const listContracts = (params = {}) =>
  api.get("/contracts", { params }).then((r) => r.data);
export const getContractsStats = () => api.get("/contracts-stats").then((r) => r.data);
export const getContract = (id) => api.get(`/contracts/${id}`).then((r) => r.data);
export const createContract = (formData, onUploadProgress) =>
  api.post("/contracts", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  }).then((r) => r.data);
export const updateContract = (id, body) =>
  api.patch(`/contracts/${id}`, body).then((r) => r.data);
export const deleteContract = (id) =>
  api.delete(`/contracts/${id}`).then((r) => r.data);
export const contractDownloadUrl = (id) => {
  const token = getAdminToken();
  return `${API}/contracts/${id}/download?token=${encodeURIComponent(token || "")}`;
};

// ---- Phase 3: Services ----
export const listAdminServices = () => api.get("/admin/services").then((r) => r.data);
export const toggleService = (id, available) =>
  api.patch(`/admin/services/${id}`, { available }).then((r) => r.data);
export const listPublicServices = () => api.get("/services-public").then((r) => r.data);

// ---- Phase 4: Tasks ----
export const TASK_ASSIGNEES = [
  { value: "beckett", label: "Beckett" },
  { value: "jazmin", label: "Jazmin" },
  { value: "shared", label: "Shared" },
];
export const listTasks = (params = {}) => api.get("/tasks", { params }).then((r) => r.data);
export const createTask = (body) => api.post("/tasks", body).then((r) => r.data);
export const updateTask = (id, body) => api.patch(`/tasks/${id}`, body).then((r) => r.data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`).then((r) => r.data);

// ---- Phase 4: Clients (derived) ----
export const listClients = (q) => api.get("/clients", { params: q ? { q } : {} }).then((r) => r.data);
export const createClientAppointment = (kind, id, body) =>
  api.post(`/clients/${kind}/${id}/appointments`, body).then((r) => r.data);

// ---- Referral partners ----
export const listReferralPartners = () => api.get("/referral-partners").then((r) => r.data);
export const getReferralPartner = (id) => api.get(`/referral-partners/${id}`).then((r) => r.data);
export const createReferralPartner = (body) => api.post("/referral-partners", body).then((r) => r.data);
export const updateReferralPartner = (id, body) => api.patch(`/referral-partners/${id}`, body).then((r) => r.data);
export const addReferralPartnerNote = (id, note) =>
  api.post(`/referral-partners/${id}/note`, { note }).then((r) => r.data);
export const archiveReferralPartner = (id) => api.post(`/referral-partners/${id}/archive`).then((r) => r.data);

// ---- Contract Patients ----
export const listContractPatients = (params = {}) =>
  api.get("/contract-patients", { params }).then((r) => r.data);
export const getContractPatient = (id) =>
  api.get(`/contract-patients/${id}`).then((r) => r.data);
export const createContractPatient = (body) =>
  api.post("/contract-patients", body).then((r) => r.data);
export const updateContractPatient = (id, body) =>
  api.patch(`/contract-patients/${id}`, body).then((r) => r.data);
export const archiveContractPatient = (id) =>
  api.post(`/contract-patients/${id}/archive`).then((r) => r.data);
export const restoreContractPatient = (id) =>
  api.post(`/contract-patients/${id}/restore`).then((r) => r.data);
export const binContractPatient = (id) =>
  api.post(`/contract-patients/${id}/bin`).then((r) => r.data);
export const unbinContractPatient = (id) =>
  api.post(`/contract-patients/${id}/unbin`).then((r) => r.data);
export const deleteContractPatient = (id) =>
  api.delete(`/contract-patients/${id}`).then((r) => r.data);
export const addContractPatientNote = (id, note) =>
  api.post(`/contract-patients/${id}/note`, { note }).then((r) => r.data);
export const getContractPatientsStats = () =>
  api.get("/contract-patients-stats").then((r) => r.data);

// ---- Standing Orders ----
export const listStandingOrders = (params = {}) =>
  api.get("/standing-orders", { params }).then((r) => r.data);
export const createStandingOrder = (body) =>
  api.post("/standing-orders", body).then((r) => r.data);
export const updateStandingOrder = (id, body) =>
  api.patch(`/standing-orders/${id}`, body).then((r) => r.data);
export const pauseStandingOrder = (id) =>
  api.post(`/standing-orders/${id}/pause`).then((r) => r.data);
export const resumeStandingOrder = (id) =>
  api.post(`/standing-orders/${id}/resume`).then((r) => r.data);
export const endStandingOrder = (id) =>
  api.post(`/standing-orders/${id}/end`).then((r) => r.data);
export const deleteStandingOrder = (id) =>
  api.delete(`/standing-orders/${id}`).then((r) => r.data);
export const materializeStandingOrderNow = (id) =>
  api.post(`/standing-orders/${id}/materialize-now`).then((r) => r.data);
export const listFacilityContractPatients = (facilityId) =>
  api.get(`/facility-leads/${facilityId}/contract-patients`).then((r) => r.data);
export const listFacilityAppointments = (facilityId, upcoming = true) =>
  api.get(`/facility-leads/${facilityId}/appointments`, { params: { upcoming } }).then((r) => r.data);

// ---- Calendar (iCal + subscribe info) ----
export const getCalendarSubscribeInfo = () =>
  api.get("/calendar/subscribe-info").then((r) => r.data);
export const runRenewalsNow = () =>
  api.post("/renewals/run-now").then((r) => r.data);
export const listPatientAppointments = (patientId) =>
  api.get(`/contract-patients/${patientId}/appointments`).then((r) => r.data);

// ---- Phase 4: Staff directory ----
export const listStaffDirectory = () => api.get("/staff-directory").then((r) => r.data);

// ---- Employee Compliance Center ----
export const listEmployees = (params = {}) =>
  api.get("/employees", { params }).then((r) => r.data);
export const getEmployeesDashboard = () =>
  api.get("/employees/dashboard").then((r) => r.data);
export const getEmployee = (id) =>
  api.get(`/employees/${id}`).then((r) => r.data);
export const createEmployee = (body) =>
  api.post("/employees", body).then((r) => r.data);
export const updateEmployee = (id, body) =>
  api.patch(`/employees/${id}`, body).then((r) => r.data);
export const deleteEmployee = (id) =>
  api.delete(`/employees/${id}`).then((r) => r.data);
export const migrateEmployees = () =>
  api.post("/employees/migrate").then((r) => r.data);
export const uploadEmployeePhoto = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/employees/${id}/photo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const employeePhotoUrl = (id) => {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/employees/${id}/photo`;
};
export const fetchEmployeePhotoBlob = (id) =>
  api.get(`/employees/${id}/photo`, { responseType: "blob" }).then((r) => r.data);
export const getEmployeeCredentials = (id) =>
  api.get(`/employees/${id}/credentials`).then((r) => r.data);
export const getEmployeeTimeline = (id) =>
  api.get(`/employees/${id}/timeline`).then((r) => r.data);
export const toggleOnboardingItem = (id, key, body) =>
  api.patch(`/employees/${id}/onboarding/${key}`, body).then((r) => r.data);
export const addOnboardingItem = (id, body) =>
  api.post(`/employees/${id}/onboarding`, body).then((r) => r.data);
export const removeOnboardingItem = (id, key) =>
  api.delete(`/employees/${id}/onboarding/${key}`).then((r) => r.data);

export const EMPLOYEE_COMPLIANCE_STATUSES = [
  { value: "new_hire",           label: "New Hire",           color: "#1D4ED8", bg: "rgba(29,78,216,0.10)" },
  { value: "onboarding",         label: "Onboarding",         color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  { value: "ready_for_dispatch", label: "Ready for Dispatch", color: "#0F6D5D", bg: "rgba(15,109,93,0.14)" },
  { value: "compliance_review",  label: "Compliance Review",  color: "#B45309", bg: "rgba(180,83,9,0.10)" },
  { value: "suspended",          label: "Suspended",          color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
  { value: "inactive",           label: "Inactive",           color: "#475569", bg: "#F1F5F9" },
];
export const EMPLOYEE_CREDENTIAL_CATEGORIES = [
  { value: "identity",              label: "Identity" },
  { value: "licenses",              label: "Licenses" },
  { value: "medical_certifications", label: "Medical Certifications" },
  { value: "training",              label: "Training" },
  { value: "background_checks",     label: "Background Checks" },
  { value: "employment_forms",      label: "Employment Forms" },
  { value: "insurance",             label: "Insurance" },
  { value: "vehicle_documents",     label: "Vehicle Documents" },
  { value: "continuing_education",  label: "Continuing Education" },
  { value: "miscellaneous",         label: "Miscellaneous" },
];

// ---- Phase 5: Dispatch ----
export const DISPATCH_LANES = [
  { value: "pending", label: "Pending", color: "#9B1C1C", bg: "rgba(155,28,28,0.08)" },
  { value: "assigned", label: "Assigned", color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  { value: "en_route", label: "En Route", color: "#0B2545", bg: "rgba(11,37,69,0.08)" },
  { value: "on_site", label: "On Site", color: "#0F6D5D", bg: "rgba(15,109,93,0.12)" },
  { value: "completed", label: "Completed", color: "#475569", bg: "#E2E8F0" },
];
export const listDispatch = () => api.get("/dispatch").then((r) => r.data);
export const getDispatchJob = (id) => api.get(`/dispatch/${id}`).then((r) => r.data);
export const updateDispatch = (leadId, body) =>
  api.patch(`/dispatch/${leadId}`, body).then((r) => r.data);
export const geocodeLead = (leadId) =>
  api.post(`/leads/${leadId}/geocode`).then((r) => r.data);
export const checkDispatchConflict = (params) =>
  api.get(`/dispatch/conflicts/check`, { params }).then((r) => r.data);
export const suggestStaffForLead = (leadId) =>
  api.get(`/dispatch/${leadId}/suggest-staff`).then((r) => r.data);
export const getStaffAvailability = () =>
  api.get(`/staff/availability`).then((r) => r.data);
export const issueStaffMobileToken = (staff_name) =>
  api.post(`/staff-mobile/issue-token`, { staff_name }).then((r) => r.data);
export const listStaffMobileTokens = () =>
  api.get(`/staff-mobile/tokens`).then((r) => r.data);
export const revokeStaffMobileToken = (staff_id) =>
  api.delete(`/staff-mobile/tokens/${staff_id}`).then((r) => r.data);

// Staff mobile portal (token-authed — NO bearer)
export const staffPortalMe = (token) =>
  axios.get(`${API}/staff-mobile/me`, { params: { token } }).then((r) => r.data);
export const staffPortalJobs = (token) =>
  axios.get(`${API}/staff-mobile/jobs`, { params: { token } }).then((r) => r.data);
export const staffPortalSetStatus = (token, leadId, dispatch_status, note) =>
  axios.post(`${API}/staff-mobile/jobs/${leadId}/status`,
    { dispatch_status, note: note || null },
    { params: { token } }).then((r) => r.data);
export const staffPortalJobSupplies = (token, leadId) =>
  axios.get(`${API}/staff-mobile/jobs/${leadId}/supplies`, { params: { token } }).then((r) => r.data);

// Dispatch chat — dual auth (staff via ?token=... OR admin bearer)
export const dispatchChatList = (leadId, { token, sinceIso } = {}) => {
  const params = {};
  if (token) params.token = token;
  if (sinceIso) params.since_iso = sinceIso;
  const client = token ? axios : api;
  const base = token ? `${API}` : "";
  return client.get(`${base}/dispatch-chat/${leadId}/messages`, { params }).then((r) => r.data);
};
export const dispatchChatSend = (leadId, body, { token } = {}) => {
  const params = token ? { token } : {};
  const client = token ? axios : api;
  const base = token ? `${API}` : "";
  return client.post(`${base}/dispatch-chat/${leadId}/messages`, body, { params }).then((r) => r.data);
};
export const dispatchChatMarkRead = (leadId, upToIso, { token } = {}) => {
  const params = token ? { token } : {};
  const client = token ? axios : api;
  const base = token ? `${API}` : "";
  return client.post(`${base}/dispatch-chat/${leadId}/read`, { up_to_iso: upToIso || null }, { params }).then((r) => r.data);
};
export const dispatchChatUpload = (leadId, file, { token } = {}) => {
  const fd = new FormData(); fd.append("file", file);
  const params = token ? { token } : {};
  const client = token ? axios : api;
  const base = token ? `${API}` : "";
  return client.post(`${base}/dispatch-chat/${leadId}/upload`, fd, {
    params, headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const dispatchChatAttachmentUrl = (leadId, fileId, { token } = {}) => {
  const q = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${API}/dispatch-chat/${leadId}/attachment/${fileId}${q}`;
};
export const dispatchChatQuickActions = ({ token } = {}) => {
  const params = token ? { token } : {};
  const client = token ? axios : api;
  const base = token ? `${API}` : "";
  return client.get(`${base}/dispatch-chat/quick-actions`, { params }).then((r) => r.data);
};
export const dispatchJobSupplies = (leadId) =>
  api.get(`/dispatch/${leadId}/supplies`).then((r) => r.data);

// Service-type specific dispatch fields (keys per service)
export const SERVICE_DETAIL_FIELDS = {
  phlebotomist: [
    { key: "lab_order", label: "Lab Order #", placeholder: "Order or req number" },
    { key: "laboratory", label: "Laboratory", placeholder: "LabCorp, Quest, in-house…" },
    { key: "fasting_status", label: "Fasting Status", type: "select",
      options: ["Not fasting", "Fasting 8h", "Fasting 12h", "Confirm with patient"] },
    { key: "collection_instructions", label: "Collection Instructions", type: "textarea" },
  ],
  courier: [
    { key: "pickup_location", label: "Pickup Location", placeholder: "Address or facility name" },
    { key: "delivery_location", label: "Delivery Location", placeholder: "Lab / destination" },
    { key: "courier_priority", label: "Courier Priority", type: "select",
      options: ["STAT", "Same-Day", "Routine"] },
  ],
  multi_patient: [
    { key: "patient_count", label: "Patient Count", type: "number" },
    { key: "collection_instructions", label: "Group Notes", type: "textarea" },
  ],
  transport: [
    { key: "destination", label: "Destination", placeholder: "Appointment address" },
    { key: "return_trip", label: "Return Trip?", type: "select",
      options: ["No — one way", "Yes — round trip", "Confirm at pickup"] },
    { key: "mobility_assist", label: "Mobility Assistance", type: "select",
      options: ["Ambulatory", "Walker", "Wheelchair", "Stretcher", "Bariatric"] },
  ],
  staffing: [
    { key: "position_requested", label: "Position Requested", placeholder: "Phlebotomist, RN, MA…" },
    { key: "coverage_type", label: "Coverage Type", type: "select",
      options: ["Single shift", "Recurring", "On-call", "PRN"] },
  ],
  clinical_assistance: [
    { key: "support_type", label: "Support Type Requested", type: "textarea",
      placeholder: "What support is needed?" },
  ],
  general: [
    { key: "notes", label: "Additional Details", type: "textarea" },
  ],
};

// ---- Phase 5: Calendar ----
export const getCalendar = (month) =>
  api.get("/calendar", { params: month ? { month } : {} }).then((r) => r.data);

// ---- Phase 5: Reports ----
export const getReportsSummary = () => api.get("/reports/summary").then((r) => r.data);
export const reportCsvUrl = (kind) => {
  const token = getAdminToken();
  return `${API}/reports/${kind}.csv?token=${encodeURIComponent(token || "")}`;
};
export const stripeSync = () => api.post("/reports/stripe-sync").then((r) => r.data);
export const createStripeCheckout = (leadId) =>
  api.post(`/leads/${leadId}/stripe-checkout`).then((r) => r.data);

export const sendPaymentRequest = (leadId) =>
  api.post(`/leads/${leadId}/send-payment-request`).then((r) => r.data);

export const refreshStripeCheckout = (leadId) =>
  api.post(`/leads/${leadId}/refresh-checkout`).then((r) => r.data);

export const regenerateInvoice = (leadId) =>
  api.post(`/leads/${leadId}/invoice/regenerate`).then((r) => r.data);

export const invoiceDownloadUrl = (leadId) => {
  const token = getAdminToken();
  return `${API}/leads/${leadId}/invoice.pdf?token=${encodeURIComponent(token || "")}`;
};

// ---- Refund Center ----
export const REFUND_STATUSES = [
  { id: "pending_verification", label: "Pending Verification", color: "#7A5E12", bg: "rgba(212,175,55,0.18)", border: "rgba(212,175,55,0.4)" },
  { id: "eligible_for_refund",  label: "Eligible for Refund",  color: "#1E3A8A", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)" },
  { id: "refund_processing",    label: "Refund Processing",    color: "#5B21B6", bg: "rgba(139,92,246,0.12)", border: "rgba(139,92,246,0.35)" },
  { id: "refunded",             label: "Refunded",             color: "#065F46", bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.4)" },
  { id: "rejected",             label: "Rejected",             color: "#991B1B", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)" },
];

export const createRefundRequest = (body) =>
  axios.post(`${API}/refunds`, body).then((r) => r.data);

export const listRefunds = (status) =>
  api.get("/refunds", { params: status ? { status } : {} }).then((r) => r.data);

export const refundsStats = () => api.get("/refunds-stats").then((r) => r.data);

export const getRefund = (id) => api.get(`/refunds/${id}`).then((r) => r.data);

export const verifyRefund = (id) => api.post(`/refunds/${id}/verify`).then((r) => r.data);

export const approveRefund = (id) => api.post(`/refunds/${id}/approve`).then((r) => r.data);

export const rejectRefund = (id, reason) =>
  api.post(`/refunds/${id}/reject`, { reason }).then((r) => r.data);

export const updateRefundNotes = (id, internal_notes) =>
  api.patch(`/refunds/${id}/notes`, { internal_notes }).then((r) => r.data);

// Ops automation analysis
export const getOpsAnalysis = (leadId) =>
  api.get(`/leads/${leadId}/ops-analysis`).then((r) => r.data);
export const refreshOpsAnalysis = (leadId, options = {}) =>
  api.post(`/leads/${leadId}/ops-analysis`, options).then((r) => r.data);

// ---- Help Center: Internal Notes ----
export const INTERNAL_NOTE_CATEGORIES = [
  { value: "business_ideas", label: "Business Ideas" },
  { value: "expansion_plans", label: "Expansion Plans" },
  { value: "future_services", label: "Future Services" },
  { value: "partnerships", label: "Partnerships" },
  { value: "vendor_notes", label: "Vendor Notes" },
  { value: "strategic_goals", label: "Strategic Goals" },
];
export const listInternalNotes = (params = {}) =>
  api.get("/internal-notes", { params }).then((r) => r.data);
export const createInternalNote = (body) =>
  api.post("/internal-notes", body).then((r) => r.data);
export const updateInternalNote = (id, body) =>
  api.patch(`/internal-notes/${id}`, body).then((r) => r.data);
export const deleteInternalNote = (id) =>
  api.delete(`/internal-notes/${id}`).then((r) => r.data);

// ---- Help Center: Supplies (inventory) ----
export const SUPPLY_CATEGORIES = [
  { value: "collection", label: "Collection" },
  { value: "specimen", label: "Specimen" },
  { value: "patient_care", label: "Patient Care" },
  { value: "safety", label: "Safety" },
  { value: "transport", label: "Transport" },
];
export const listSupplies = () => api.get("/supplies").then((r) => r.data);
export const getSuppliesStats = () => api.get("/supplies-stats").then((r) => r.data);
export const createSupply = (body) => api.post("/supplies", body).then((r) => r.data);
export const updateSupply = (id, body) => api.patch(`/supplies/${id}`, body).then((r) => r.data);
export const adjustSupply = (id, delta) =>
  api.post(`/supplies/${id}/adjust`, { delta }).then((r) => r.data);
export const deleteSupply = (id) => api.delete(`/supplies/${id}`).then((r) => r.data);

// ---- Facility / Provider pipeline ----
export const listFacilityLeads = () =>
  api.get("/facility-leads").then((r) => r.data);
export const getFacilityLead = (id) =>
  api.get(`/facility-leads/${id}`).then((r) => r.data);
export const getFacilityStats = () =>
  api.get("/facility-leads-stats").then((r) => r.data);
export const updateFacilityLead = (id, body) =>
  api.patch(`/facility-leads/${id}`, body).then((r) => r.data);
export const lockFacilityContract = (id) =>
  api.post(`/facility-leads/${id}/lock-contract`).then((r) => r.data);
export const addFacilityNote = (id, note) =>
  api.post(`/facility-leads/${id}/note`, { note }).then((r) => r.data);
export const sendFacilityEmail = (id, body) =>
  api.post(`/facility-leads/${id}/email`, body).then((r) => r.data);
export const deleteFacilityLead = (id) =>
  api.delete(`/facility-leads/${id}`).then((r) => r.data);
export const archiveFacilityLead = (id) =>
  api.post(`/facility-leads/${id}/archive`).then((r) => r.data);
export const restoreFacilityLead = (id) =>
  api.post(`/facility-leads/${id}/restore`).then((r) => r.data);
export const binFacilityLead = (id) =>
  api.post(`/facility-leads/${id}/bin`).then((r) => r.data);
export const unbinFacilityLead = (id) =>
  api.post(`/facility-leads/${id}/unbin`).then((r) => r.data);

export const FACILITY_STATUSES = [
  // New 8-stage pipeline (Feb 2026 redesign — user-facing labels)
  { value: "new_request",      label: "New Request",      color: "#7A5E12", bg: "rgba(212,175,55,0.16)", border: "rgba(212,175,55,0.4)" },
  { value: "under_review",     label: "Under Review",     color: "#0B2545", bg: "rgba(11,37,69,0.08)",   border: "rgba(11,37,69,0.25)" },
  { value: "quote_prepared",   label: "Quote Prepared",   color: "#0F766E", bg: "rgba(20,184,166,0.14)", border: "rgba(20,184,166,0.35)" },
  { value: "proposal_sent",    label: "Proposal Sent",    color: "#1D4ED8", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)" },
  { value: "negotiating",      label: "Negotiating",      color: "#9A3412", bg: "rgba(234,88,12,0.14)",  border: "rgba(234,88,12,0.4)" },
  { value: "contract_pending", label: "Contract Pending", color: "#1D4ED8", bg: "rgba(59,130,246,0.14)", border: "rgba(59,130,246,0.4)" },
  { value: "active",           label: "Active",           color: "#065F46", bg: "rgba(16,185,129,0.16)", border: "rgba(16,185,129,0.45)" },
  { value: "closed",           label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  // Side-flags — kept for existing records
  { value: "priority_partner", label: "Priority Partner", color: "#6B21A8", bg: "rgba(168,85,247,0.14)", border: "rgba(168,85,247,0.4)" },
  { value: "needs_followup",   label: "Needs Follow-Up",  color: "#991B1B", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.3)" },
  // Legacy values — kept for existing records (shown with new label)
  { value: "new_inquiry",      label: "New Request",      color: "#7A5E12", bg: "rgba(212,175,55,0.16)", border: "rgba(212,175,55,0.4)" },
  { value: "contacted",        label: "Under Review",     color: "#0B2545", bg: "rgba(11,37,69,0.08)",   border: "rgba(11,37,69,0.25)" },
  { value: "needs_assessment", label: "Under Review",     color: "#0B2545", bg: "rgba(11,37,69,0.08)",   border: "rgba(11,37,69,0.25)" },
  { value: "quote_sent",       label: "Proposal Sent",    color: "#1D4ED8", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)" },
  { value: "onboarding",       label: "Active",           color: "#065F46", bg: "rgba(16,185,129,0.16)", border: "rgba(16,185,129,0.45)" },
  { value: "active_client",    label: "Active",           color: "#065F46", bg: "rgba(16,185,129,0.16)", border: "rgba(16,185,129,0.45)" },
  { value: "archived_stage",   label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  { value: "lost_deal",        label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  { value: "follow_up_needed", label: "Needs Follow-Up",  color: "#991B1B", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.3)" },
  { value: "trial_service_started", label: "Active",      color: "#065F46", bg: "rgba(16,185,129,0.16)", border: "rgba(16,185,129,0.45)" },
  { value: "contract_active",  label: "Active",           color: "#065F46", bg: "rgba(16,185,129,0.16)", border: "rgba(16,185,129,0.45)" },
  { value: "on_hold",          label: "On Hold",          color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  { value: "declined",         label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  { value: "lost_opportunity", label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
  { value: "contract_expired", label: "Closed",           color: "#475569", bg: "rgba(148,163,184,0.18)", border: "rgba(148,163,184,0.4)" },
];

// New 8-stage pipeline — the ONLY values a new record should ever be set to.
export const FACILITY_PIPELINE_STAGES = [
  "new_request", "under_review", "quote_prepared", "proposal_sent",
  "negotiating", "contract_pending", "active", "closed",
];

export const FACILITY_STATUS_ALIAS = {
  // Legacy → new canonical
  new_inquiry:            "new_request",
  contacted:              "under_review",
  needs_assessment:       "under_review",
  quote_sent:             "proposal_sent",
  onboarding:             "active",
  active_client:          "active",
  trial_service_started:  "active",
  contract_active:        "active",
  archived_stage:         "closed",
  lost_deal:              "closed",
  declined:               "closed",
  lost_opportunity:       "closed",
  contract_expired:       "closed",
  follow_up_needed:       "needs_followup",
};

export const canonicalFacilityStatus = (s) => FACILITY_STATUS_ALIAS[s] || s;

export const FACILITY_LOST_STATUSES = ["closed", "lost_deal", "declined", "lost_opportunity", "contract_expired"];

// 8-step linear pipeline for the stepper + Move to Next Stage.
export const FACILITY_CORE_PIPELINE = [
  { value: "new_request",      label: "New Request",      short: "New Request" },
  { value: "under_review",     label: "Under Review",     short: "Under Review" },
  { value: "quote_prepared",   label: "Quote Prepared",   short: "Quote Prepared" },
  { value: "proposal_sent",    label: "Proposal Sent",    short: "Proposal Sent" },
  { value: "negotiating",      label: "Negotiating",      short: "Negotiating" },
  { value: "contract_pending", label: "Contract Pending", short: "Contract Pending" },
  { value: "active",           label: "Active",           short: "Active" },
  { value: "closed",           label: "Closed",           short: "Closed" },
];

export const FACILITY_STAGE_INDEX = (s) => {
  const c = canonicalFacilityStatus(s);
  const idx = FACILITY_CORE_PIPELINE.findIndex((x) => x.value === c);
  return idx; // -1 for side-flag stages (needs_followup, priority_partner)
};

// Recommended "Next Action" per pipeline stage — surfaced on the modal.
export const STAGE_RECOMMENDED_ACTION = {
  new_request:      "Review request and assess facility needs.",
  under_review:     "Confirm requirements and schedule a discovery call.",
  quote_prepared:   "Finalize pricing and prepare the proposal document.",
  proposal_sent:    "Follow up with the facility to confirm receipt and answer questions.",
  negotiating:      "Negotiate terms, pricing adjustments, and contract clauses.",
  contract_pending: "Chase signature on the contract. Send a gentle reminder if 3+ days.",
  active:           "Verify first visit went smoothly and send a satisfaction check.",
  closed:           "Archive the request. Record the outcome in Notes & Activity.",
  priority_partner: "Nurture this relationship — monthly check-in and QBR.",
  needs_followup:   "A commitment is overdue. Call today to re-engage.",
};

// ---- Facility CRM Phase 1 — activity + AI ----
export const logFacilityActivity = (id, body) =>
  api.post(`/facility-leads/${id}/activity`, body).then((r) => r.data);

export const facilityAIAnalysis = (id) =>
  api.post(`/facility-leads/${id}/ai-analysis`).then((r) => r.data);

export const facilityAIChat = (id, body) =>
  api.post(`/facility-leads/${id}/ai-chat`, body).then((r) => r.data);

export const advanceFacilityStage = (id) =>
  api.post(`/facility-leads/${id}/advance-stage`).then((r) => r.data);

export const setFacilityStage = (id, stage) =>
  api.post(`/facility-leads/${id}/set-stage`, { stage }).then((r) => r.data);

export const listFacilityEmails = (id) =>
  api.get(`/facility-leads/${id}/emails`).then((r) => r.data);

export const markEmailReplied = (msgId) =>
  api.post(`/email-messages/${msgId}/mark-replied`).then((r) => r.data);

// ---- Facility Proposals (Phase 2) ----
export const listFacilityProposals = (facId) =>
  api.get(`/facility-leads/${facId}/proposals`).then((r) => r.data);
export const createFacilityProposal = (facId, body) =>
  api.post(`/facility-leads/${facId}/proposals`, body).then((r) => r.data);
export const getProposal = (id) =>
  api.get(`/proposals/${id}`).then((r) => r.data);
export const updateProposal = (id, body) =>
  api.patch(`/proposals/${id}`, body).then((r) => r.data);
export const deleteProposal = (id) =>
  api.delete(`/proposals/${id}`).then((r) => r.data);
export const regenerateProposalPdf = (id) =>
  api.post(`/proposals/${id}/generate-pdf`).then((r) => r.data);
export const sendProposal = (id, body = {}) =>
  api.post(`/proposals/${id}/send`, body).then((r) => r.data);
export const markProposalAccepted = (id) =>
  api.post(`/proposals/${id}/mark-accepted`).then((r) => r.data);
export const markProposalDeclined = (id) =>
  api.post(`/proposals/${id}/mark-declined`).then((r) => r.data);
export const proposalPdfUrl = (id) => {
  const token = getAdminToken();
  return `${API}/proposals/${id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ""}`;
};

export const PROPOSAL_STATUS_META = {
  draft:    { label: "Draft",    color: "#475569", bg: "rgba(148,163,184,0.16)", border: "rgba(148,163,184,0.4)" },
  sent:     { label: "Sent",     color: "#1D4ED8", bg: "rgba(59,130,246,0.14)",  border: "rgba(59,130,246,0.4)"  },
  accepted: { label: "Accepted", color: "#065F46", bg: "rgba(16,185,129,0.16)",  border: "rgba(16,185,129,0.5)"  },
  declined: { label: "Declined", color: "#991B1B", bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.35)"  },
  expired:  { label: "Expired",  color: "#7A5E12", bg: "rgba(212,175,55,0.16)",  border: "rgba(212,175,55,0.4)"  },
};

export const PROPOSAL_UNITS = [
  { value: "each",      label: "Each" },
  { value: "per_month", label: "Per month" },
  { value: "per_draw",  label: "Per draw" },
  { value: "per_visit", label: "Per visit" },
  { value: "per_hour",  label: "Per hour" },
  { value: "flat",      label: "Flat / one-time" },
];

// ---- Facility Workflow: Contract / AI Email / Invoice / Messages ----
export const generateFacilityContract = (facId, body = {}) =>
  api.post(`/facility-leads/${facId}/generate-contract`, body).then((r) => r.data);
export const generateFacilityEmailTemplate = (facId, body) =>
  api.post(`/facility-leads/${facId}/ai-email-template`, body).then((r) => r.data);
export const generateFacilityInvoice = (facId, body) =>
  api.post(`/facility-leads/${facId}/generate-invoice`, body).then((r) => r.data);
export const listFacilityInvoices = (facId) =>
  api.get(`/facility-leads/${facId}/invoices`).then((r) => r.data);
export const getFacilityMessages = (facId) =>
  api.get(`/facility-leads/${facId}/messages`).then((r) => r.data);

export const EMAIL_TEMPLATE_KINDS = [
  { value: "introduction",     label: "Introduction",       stage: "new_inquiry" },
  { value: "follow_up",        label: "Follow-Up",          stage: "contacted" },
  { value: "needs_assessment", label: "Needs Assessment",   stage: "needs_assessment" },
  { value: "quote_delivery",   label: "Quote Delivery",     stage: "quote_sent" },
  { value: "negotiation",      label: "Negotiation",        stage: "negotiating" },
  { value: "contract_ready",   label: "Contract Ready",     stage: "contract_pending" },
  { value: "welcome",          label: "Welcome / Onboarding", stage: "onboarding" },
  { value: "renewal",          label: "Renewal",            stage: "active_client" },
];

export const FACILITY_INVOICE_STATUS_META = {
  pending:   { label: "Pending",   color: "#7A5E12", bg: "rgba(212,175,55,0.16)" },
  sent:      { label: "Sent",      color: "#1D4ED8", bg: "rgba(59,130,246,0.14)" },
  paid:      { label: "Paid",      color: "#065F46", bg: "rgba(16,185,129,0.16)" },
  overdue:   { label: "Overdue",   color: "#B91C1C", bg: "rgba(220,38,38,0.10)" },
  cancelled: { label: "Cancelled", color: "#64748B", bg: "rgba(148,163,184,0.16)" },
};

// ---- Lead Communications ----
export const leadRef = (leadId) => `LEAD-${(leadId || "").replace(/-/g, "").slice(0, 8).toUpperCase()}`;
export const getLeadCommunications = (leadId) =>
  api.get(`/leads/${leadId}/communications`).then((r) => r.data);
export const sendLeadEmail = (leadId, body) =>
  api.post(`/leads/${leadId}/communications/send`, body).then((r) => r.data);
export const generateLeadAIReply = (leadId, body = {}) =>
  api.post(`/leads/${leadId}/communications/ai-reply`, body).then((r) => r.data);
export const addLeadInternalNote = (leadId, text) =>
  api.post(`/leads/${leadId}/communications/notes`, { text }).then((r) => r.data);
export const deleteLeadInternalNote = (leadId, noteId) =>
  api.delete(`/leads/${leadId}/communications/notes/${noteId}`).then((r) => r.data);
export const sendLeadPaymentLink = (leadId, body) =>
  api.post(`/leads/${leadId}/communications/payment-link`, body).then((r) => r.data);
export const sendLeadInvoice = (leadId, body) =>
  api.post(`/leads/${leadId}/communications/invoice`, body).then((r) => r.data);
export const sendLeadAppointmentConfirmation = (leadId, body) =>
  api.post(`/leads/${leadId}/communications/appointment`, body).then((r) => r.data);
export const sendLeadReminder = (leadId, body = {}) =>
  api.post(`/leads/${leadId}/communications/reminder`, body).then((r) => r.data);
export const uploadLeadAttachment = (leadId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/leads/${leadId}/communications/attachments`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const leadAttachmentUrl = (leadId, fileId) => {
  const token = getAdminToken();
  return `${API}/leads/${leadId}/communications/attachments/${fileId}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
};

// ---- Gmail OAuth (per-user) ----
export const getGmailStatus = () => api.get("/oauth/gmail/status").then((r) => r.data);
export const startGmailAuth = () => api.get("/oauth/gmail/authorize").then((r) => r.data);
export const disconnectGmail = () => api.post("/oauth/gmail/disconnect").then((r) => r.data);
export const syncGmailInbox = () => api.post("/oauth/gmail/sync").then((r) => r.data);
export const sendViaGmail = (body) => api.post("/gmail/send", body).then((r) => r.data);

export const EMAIL_STATUS_META = {
  queued:    { label: "Queued",    color: "#7A5E12", bg: "rgba(212,175,55,0.15)", border: "rgba(212,175,55,0.35)" },
  sent:      { label: "Sent",      color: "#1D4ED8", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)" },
  delivered: { label: "Delivered", color: "#0F6D5D", bg: "rgba(20,184,166,0.14)", border: "rgba(20,184,166,0.4)"  },
  opened:    { label: "Opened",    color: "#5B21B6", bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.4)"  },
  clicked:   { label: "Clicked",   color: "#065F46", bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.4)"  },
  replied:   { label: "Replied",   color: "#065F46", bg: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.5)"  },
  bounced:   { label: "Bounced",   color: "#991B1B", bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.35)"  },
  failed:    { label: "Failed",    color: "#7F1D1D", bg: "rgba(220,38,38,0.08)",  border: "rgba(220,38,38,0.3)"   },
};

export const BILLING_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
];

export const PAYMENT_TERMS = [
  { value: "net_15", label: "Net 15" },
  { value: "net_30", label: "Net 30" },
  { value: "net_45", label: "Net 45" },
  { value: "net_60", label: "Net 60" },
];

export const PRICING_MODELS = [
  { value: "per_visit", label: "Per Visit" },
  { value: "bundled", label: "Bundled Services" },
  { value: "monthly_retainer", label: "Monthly Retainer" },
  { value: "hybrid", label: "Hybrid" },
];

export const FACILITY_EMAIL_ACTIONS = [
  { value: "intro_followup", label: "Intro Follow-Up" },
  { value: "schedule_call", label: "Schedule Discovery Call" },
  { value: "send_quote", label: "Send Quote" },
  { value: "proposal_ready", label: "Proposal Ready" },
  { value: "follow_up", label: "General Follow-Up" },
  { value: "invoice", label: "Send Invoice" },
  { value: "contract_welcome", label: "Contract Welcome" },
];

export const PAYMENT_STATUSES = [
  { value: "unpaid", label: "Unpaid" },
  { value: "needs_pricing", label: "Needs Pricing" },
  { value: "custom_payment_created", label: "Custom Payment Created" },
  { value: "payment_link_sent", label: "Payment Sent" },
  { value: "paid", label: "Paid" },
  { value: "partial_payment", label: "Partial Payment" },
  { value: "remaining_balance", label: "Remaining Balance" },
  { value: "confirmed_scheduled", label: "Confirmed / Scheduled" },
  { value: "cancelled", label: "Cancelled / Refunded" },
];

export const PRICING_TYPES = [
  { value: "Standard", label: "Standard" },
  { value: "Rush", label: "Rush" },
  { value: "Custom Payment", label: "Custom Payment" },
  { value: "Facility / Provider", label: "Facility / Provider" },
];

export const SERVICES = [
  {
    id: "phlebotomist",
    label: "Phlebotomist Service",
    description: "Mobile blood draws and specimen collection.",
    icon: "Droplet",
  },
  {
    id: "courier",
    label: "Medical Courier Service",
    description: "STAT delivery of specimens, supplies & records.",
    icon: "Truck",
  },
  {
    id: "multi_patient",
    label: "Multi-Patient Collection",
    description: "On-site visits for households or small groups.",
    icon: "Users",
  },
  {
    id: "transport",
    label: "Non-Emergency Patient Transportation",
    description: "Safe, dignified rides to appointments.",
    icon: "Ambulance",
  },
  {
    id: "staffing",
    label: "Medical Staffing Support",
    description: "Phlebotomists, MAs, CNAs — one-time, temporary, or ongoing coverage.",
    icon: "Stethoscope",
  },
  {
    id: "clinical_assistance",
    label: "Clinical Assistant Services",
    description: "Intake, scheduling, administrative & clinical support.",
    icon: "Stethoscope",
  },
  {
    id: "general",
    label: "General Inquiry",
    description: "Not sure? Tell us what you need.",
    icon: "HelpCircle",
  },
];

export const URGENCY = [
  "Standard",
  "Priority",
  "Same Day",
  "Immediate",
];

export const URGENCY_LABELS = {
  Standard: "Standard",
  Priority: "Priority (24–48 Hours)",
  "Same Day": "Urgent (Same Day)",
  Immediate: "STAT / Immediate Review",
};

// Lab order upload (public — for phlebotomy intake)
export const uploadLabOrder = (leadId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/leads/${leadId}/lab-order`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const labOrderDownloadUrl = (leadId) => {
  const token = getAdminToken();
  return `${API}/leads/${leadId}/lab-order?token=${encodeURIComponent(token || "")}`;
};

// ---- Documents (admin-only document vault) ----
export const DOCUMENT_CATEGORIES = [
  { value: "business", label: "Business Documents" },
  { value: "staff", label: "Staff Documents" },
  { value: "facilities_providers", label: "Facilities & Providers" },
  { value: "clients", label: "Client Documents" },
  { value: "financial", label: "Financial Documents" },
];

export const listDocuments = (params = {}) =>
  api.get("/documents", { params }).then((r) => r.data);

// ---- Documents v2 (Enterprise DMS) ----
export const v2ListDocuments = (params = {}) =>
  api.get("/documents-v2", { params }).then((r) => r.data);
export const v2Dashboard = () =>
  api.get("/documents-v2/dashboard").then((r) => r.data);
export const v2Categories = () =>
  api.get("/documents-v2/categories").then((r) => r.data);
export const v2Migrate = () =>
  api.post("/documents-v2/migrate").then((r) => r.data);
export const v2GetDocument = (id) =>
  api.get(`/documents-v2/${id}`).then((r) => r.data);
export const v2UpdateDocument = (id, body) =>
  api.patch(`/documents-v2/${id}`, body).then((r) => r.data);
export const v2ArchiveDocument = (id) =>
  api.post(`/documents-v2/${id}/archive`).then((r) => r.data);
export const v2RestoreDocument = (id) =>
  api.post(`/documents-v2/${id}/restore`).then((r) => r.data);
export const v2DeleteDocument = (id) =>
  api.delete(`/documents-v2/${id}`).then((r) => r.data);
export const v2DownloadUrl = (id) => {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/documents-v2/${id}/download`;
};
export const v2FetchDownloadBlob = (id) =>
  api.get(`/documents-v2/${id}/download`, { responseType: "blob" }).then((r) => r.data);
export const v2Upload = (formData, onUploadProgress) =>
  api.post("/documents-v2/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  }).then((r) => r.data);

export const V2_ENTITY_LABEL = {
  business:         "Business",
  employee:         "Employee",
  facility:         "Facility",
  contract_patient: "Contract Patient",
  consumer:         "Consumer",
  lead:             "Lead",
  contract:         "Contract",
  financial:        "Financial",
  vendor:           "Vendor",
};

export const V2_ROOT_LABEL = {
  business:  "Business Documents",
  employee:  "Staff Documents",
  facility:  "Facility Documents",
  client:    "Client Documents",
  financial: "Financial Documents",
};

export const getDocumentStats = () =>
  api.get("/documents-stats").then((r) => r.data);

export const uploadDocument = (formData, onUploadProgress) =>
  api
    .post("/documents/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);

export const updateDocument = (id, body) =>
  api.patch(`/documents/${id}`, body).then((r) => r.data);

export const deleteDocument = (id) =>
  api.delete(`/documents/${id}`).then((r) => r.data);

export const documentDownloadUrl = (id) => {
  const token = getAdminToken();
  return `${API}/documents/${id}/download?token=${encodeURIComponent(token || "")}`;
};

// ---- Operations & Documents (staff/company records vault) ----
export const STAFF_DOC_GROUPS = [
  {
    value: "employee_staff",
    label: "Employee & Staff Documents",
    subcategories: [
      { value: "employee_profiles", label: "Employee Profiles" },
      { value: "id_uploads", label: "ID Uploads" },
      { value: "certifications", label: "Certifications" },
      { value: "cpr_bls", label: "CPR / BLS" },
      { value: "licenses", label: "Licenses" },
      { value: "onboarding_forms", label: "Onboarding Forms" },
      { value: "payroll_tax_forms", label: "Payroll / Tax Forms" },
      { value: "background_checks", label: "Background Checks" },
    ],
  },
  {
    value: "company",
    label: "Company Documents",
    subcategories: [
      { value: "ein_tax", label: "EIN / Tax" },
      { value: "insurance_policies", label: "Insurance Policies" },
      { value: "contracts", label: "Contracts" },
      { value: "vendor_agreements", label: "Vendor Agreements" },
      { value: "business_licenses", label: "Business Licenses" },
      { value: "operating_documents", label: "Operating Documents" },
      { value: "compliance_records", label: "Compliance Records" },
    ],
  },
];

export const STAFF_SUBCATEGORY_LABEL = STAFF_DOC_GROUPS.reduce((acc, g) => {
  g.subcategories.forEach((s) => { acc[s.value] = s.label; });
  return acc;
}, {});

export const listStaffDocuments = (params = {}) =>
  api.get("/staff-documents", { params }).then((r) => r.data);
export const getStaffDocumentStats = () =>
  api.get("/staff-documents-stats").then((r) => r.data);
export const uploadStaffDocument = (formData, onUploadProgress) =>
  api.post("/staff-documents/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  }).then((r) => r.data);
export const updateStaffDocument = (id, body) =>
  api.patch(`/staff-documents/${id}`, body).then((r) => r.data);
export const replaceStaffDocument = (id, formData, onUploadProgress) =>
  api.post(`/staff-documents/${id}/replace`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  }).then((r) => r.data);
export const archiveStaffDocument = (id) =>
  api.post(`/staff-documents/${id}/archive`).then((r) => r.data);
export const restoreStaffDocument = (id) =>
  api.post(`/staff-documents/${id}/restore`).then((r) => r.data);
export const deleteStaffDocument = (id) =>
  api.delete(`/staff-documents/${id}`).then((r) => r.data);
export const staffDocumentDownloadUrl = (id) => {
  const token = getAdminToken();
  return `${API}/staff-documents/${id}/download?token=${encodeURIComponent(token || "")}`;
};

// ---- Settings → Integrations & Secrets (Owner only, requires re-auth header) ----
export const settingsReauth = (password) =>
  api.post("/admin/settings/reauth", { password }).then((r) => r.data);

const reauthHeaders = (reauthToken) => ({
  headers: { "X-Reauth-Token": reauthToken || "" },
});

export const listIntegrations = (reauthToken) =>
  api.get("/admin/integrations", reauthHeaders(reauthToken)).then((r) => r.data);

export const updateIntegrationSecrets = (service, updates, reauthToken) =>
  api
    .put(`/admin/integrations/${service}/secrets`, { updates }, reauthHeaders(reauthToken))
    .then((r) => r.data);

export const deleteIntegrationSecret = (service, keyName, reauthToken) =>
  api
    .delete(`/admin/integrations/${service}/secret/${keyName}`, reauthHeaders(reauthToken))
    .then((r) => r.data);

export const testIntegration = (service, reauthToken) =>
  api
    .post(`/admin/integrations/${service}/test`, {}, reauthHeaders(reauthToken))
    .then((r) => r.data);

export const getIntegrationsAudit = (reauthToken, limit = 50) =>
  api
    .get("/admin/integrations/audit", { params: { limit }, ...reauthHeaders(reauthToken) })
    .then((r) => r.data);

// ---- T&J Operations AI assistant ----
export const aiChatHistory = () => api.get("/ai/chat/history").then((r) => r.data);
export const aiChat = (message, context = null) =>
  api.post("/ai/chat", { message, context }).then((r) => r.data);
export const aiChatAction = (action) => api.post("/ai/chat/action", action).then((r) => r.data);
export const aiAudit = (limit = 50) =>
  api.get("/ai/audit", { params: { limit } }).then((r) => r.data);
export const aiClearHistory = () => api.delete("/ai/chat/history").then((r) => r.data);

